import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { ApiError } from '../api/client'
import { companiesApi } from '../api/companies'
import type { QboCompanySummary } from '../api/types'
import { useAuth } from '../auth/useAuth'
import { CompanyContext, companyKeyFor } from './CompanyContext'
import type { CompanyState } from './CompanyContext'

const isDisconnected = (company: QboCompanySummary) => company.status === 'Disconnected'

/**
 * Holds the tenant's QuickBooks companies for the whole signed-in app.
 *
 * The list is deliberately unfiltered: the Switch Company screen shows
 * disconnected rows behind its "Show disconnected companies" toggle, because a
 * disconnect keeps the row and its history rather than deleting it. Everything
 * that *acts* on a company — the top-bar switcher, the wizards, the backup
 * picker — wants only the live ones, so those read {@link CompanyState.connected}.
 */
export function CompanyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()

  // Scoped to the signed-in tenant, not just the browser. The provider is
  // mounted once for the whole app and outlives a sign-out, so without this a
  // second account signing in on the same tab inherited the first account's
  // company list — showing another tenant's QuickBooks companies as their own.
  const tenantId = user?.tenantId ?? null

  const [companies, setCompanies] = useState<QboCompanySummary[]>([])
  const [currentId, setCurrentId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)

  // Nothing to load until there is a session, and a signed-out tab must not keep
  // the previous tenant's list on screen.
  useEffect(() => {
    if (!tenantId) {
      setCompanies([])
      setCurrentId(null)
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    companiesApi
      .list()
      .then((list) => {
        if (cancelled) return
        setCompanies(list)
        setError(null)

        // Seeded from storage per tenant, so a switch between accounts cannot
        // carry the previous one's selection across.
        const stored = localStorage.getItem(companyKeyFor(tenantId))

        // The active context has to be a company that can actually run a job.
        // Falling back only when the stored id is *absent from the list* is not
        // enough: a disconnected company stays in the list, so after a
        // disconnect the app would keep pointing at it and every subsequent
        // job would fail on a token that is never coming back.
        const usable = list.filter((c) => !isDisconnected(c))
        setCurrentId(
          stored && usable.some((c) => c.id === stored) ? stored : (usable[0]?.id ?? null),
        )
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof ApiError ? cause : new ApiError(0, 'NETWORK', String(cause)))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [tenantId, tick])

  useEffect(() => {
    if (!tenantId) return

    if (currentId) {
      localStorage.setItem(companyKeyFor(tenantId), currentId)
    } else {
      // Nothing is selectable, so drop the key rather than leaving a stale id
      // that would be handed back to the next session.
      localStorage.removeItem(companyKeyFor(tenantId))
    }
  }, [tenantId, currentId])

  const select = useCallback((id: string) => setCurrentId(id), [])
  const reload = useCallback(() => {
    setLoading(true)
    setTick((n) => n + 1)
  }, [])

  const connected = useMemo(() => companies.filter((c) => !isDisconnected(c)), [companies])

  const value = useMemo<CompanyState>(
    () => ({
      companies,
      connected,
      current: connected.find((c) => c.id === currentId) ?? null,
      loading,
      error,
      select,
      reload,
    }),
    [companies, connected, currentId, loading, error, select, reload],
  )

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
}
